
import { isValidPhoneNumber, getCountryCallingCode, getCountries } from 'libphonenumber-js/max';

const getCountryName = (countryCode) => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
};

const findCountryByCallingCode = (callingCode) => {
  const callingCodeNumber = callingCode.replace('+', '');
  const supportedCountries = getCountries();
  
  for (const countryCode of supportedCountries) {
    try {
      if (getCountryCallingCode(countryCode) === callingCodeNumber) {
        return countryCode;
      }
    } catch {}
  }
  return null;
};

export const validateCompanyField = (name, value, mobileCode = null) => {
  const trimmedValue = value?.toString().trim() || '';
  const requiredFields = {
    companyName: 'Company name',
    companyCode: 'Company code',
    companyEmail: 'Company email',
    mobileCode: 'Mobile code',
    mobileNumber: 'Mobile number'
  };
  
  if (requiredFields[name] && !trimmedValue) {
    return `${requiredFields[name]} is required`;
  }
  
  switch (name) {
    case 'companyName':
      if (trimmedValue.length < 3) {
        return 'Company name must be at least 3 characters';
      }
      if (trimmedValue.length > 100) {
        return 'Company name must be less than 100 characters';
      }
      break;

    case 'companyEmail':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (trimmedValue && !emailRegex.test(trimmedValue)) {
        return 'Invalid email format';
      }
      break;

    case 'mobileCode':
      if (trimmedValue && !trimmedValue.startsWith('+')) {
        return 'Mobile code must start with +';
      }
      if (trimmedValue && !/^\+\d{1,4}$/.test(trimmedValue)) {
        return 'Invalid mobile code format';
      }
      break;

    case 'mobileNumber':
      if (trimmedValue && !/^\d+$/.test(trimmedValue)) {
        return 'Mobile number must contain only digits';
      }
      
      // Dynamic validation using libphonenumber-js
      if (trimmedValue && mobileCode) {
        try {
          const fullNumber = `${mobileCode}${trimmedValue}`;
          
          // Find country code from mobile code
          const countryCode = findCountryByCallingCode(mobileCode);
          
          if (!isValidPhoneNumber(fullNumber, countryCode)) {
            const countryName = countryCode ? getCountryName(countryCode) : 'selected country';
            return `Invalid mobile number for ${countryName}`;
          }
        } catch {
          return 'Invalid mobile number format';
        }
      } else if (trimmedValue && (trimmedValue.length < 7 || trimmedValue.length > 15)) {
        return 'Mobile number must be between 7 and 15 digits';
      }
      break;

    case 'zipCode':
      if (trimmedValue && trimmedValue.length !== 6) {
        return 'Zip code must be 6 digits';
      }
      if (trimmedValue && !/^\d+$/.test(trimmedValue)) {
        return 'Zip code must contain only digits';
      }
      break;

    case 'gstNo':
      if (trimmedValue) {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(trimmedValue)) {
          return 'Invalid GST number format (e.g., 22AAAAA0000A1Z5)';
        }
      }
      break;

    case 'companyWebsite':
      if (trimmedValue) {
        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
        if (!urlRegex.test(trimmedValue)) {
          return 'Invalid website URL format';
        }
      }
      break;

    case 'addressLine1':
      if (trimmedValue && trimmedValue.length < 5) {
        return 'Address must be at least 5 characters';
      }
      break;

    case 'city':
      if (trimmedValue && trimmedValue.length < 2) {
        return 'City name must be at least 2 characters';
      }
      break;

    case 'state':
      if (trimmedValue && trimmedValue.length < 2) {
        return 'State name must be at least 2 characters';
      }
      break;

    default:
      break;
  }

  return ''; 
};

export const validateAllFields = (formData) => {
  const errors = {};
  const fieldsToValidate = [
    'companyName',
    'companyCode',
    'companyEmail',
    'mobileCode',
    'mobileNumber',
    'city',
    'state',
    'country',
    'zipCode',
    'addressLine1',
    'gstNo',
    'companyWebsite',
    'industry',
    'companySize'
  ];

  fieldsToValidate.forEach(fieldName => {
    const error = validateCompanyField(
      fieldName, 
      formData[fieldName], 
      fieldName === 'mobileNumber' ? formData.mobileCode : null
    );
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};
